<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import {
  AdminEditorTopBar,
  UiChip,
  UiSegmentedControl,
  UiInfoBox,
  UiStandardButton,
  UiText,
  useToaster,
} from '@shopexperts/shared';
import type { PageSchema, PageSchemaField, PageSchemaFieldType, Searchability } from '@shopexperts/shared';
import { useSchema } from '../composables/useSchema';
import { useCreateSchemaFlow } from '../composables/useCreateSchemaFlow';
import { usePageSchemas } from '../composables/usePageSchemas';
import { useUpdateSchema } from '../composables/useUpdateSchema';
import {
  createDefaultSchemaField,
  formatMetafieldReference,
  getPageFieldTypeDefinition,
  validateSchemaFields,
} from '../field-types';
import AddSchemaFieldDialog from '../components/schema-builder/AddSchemaFieldDialog.vue';
import CreateSchemaDialog from '../components/schema-builder/CreateSchemaDialog.vue';
import SchemaFieldDefinitionInspector from '../components/schema-builder/SchemaFieldDefinitionInspector.vue';
import SchemaFieldList from '../components/schema-builder/SchemaFieldList.vue';
import SchemaJsonPanel from '../components/schema-builder/SchemaJsonPanel.vue';
import SchemaNavigatorPanel from '../components/schema-builder/SchemaNavigatorPanel.vue';
import SchemaSettingsPanel from '../components/schema-builder/SchemaSettingsPanel.vue';
import type { SchemaDraft } from '../utils/schemaJson';

const route = useRoute();
const router = useRouter();
const toaster = useToaster();

const schemaId = computed(() => {
  const id = route.params.schemaId;
  return typeof id === 'string' ? parseInt(id, 10) : 0;
});

const { schema, isLoading, error } = useSchema(schemaId);
const { schemas, isLoading: isLoadingSchemas } = usePageSchemas();
const { mutateAsync: updateSchema, isPending: isSaving } = useUpdateSchema();
const createFlow = useCreateSchemaFlow();

const name = ref('');
const label = ref('');
const description = ref('');
const searchability = ref<Searchability>('non-searchable');
const groupLabel = ref('');
const groupViewAllLabel = ref('');
const groupViewAllHref = ref('');
const searchSortIndex = ref<number | null>(null);
const fields = ref<PageSchemaField[]>([]);
const selectedIndex = ref(0);
const schemasPanelOpen = ref(true);
const schemaSearch = ref('');
const addFieldDialogOpen = ref(false);
const hydratedSchemaId = ref<number | null>(null);

const editorView = ref<'form' | 'json'>('form');

const selectedField = computed(() => fields.value[selectedIndex.value] ?? null);
const validationErrors = computed(() => validateSchemaFields(fields.value));
const canSave = computed(() => Boolean(schema.value && name.value.trim() && label.value.trim() && validationErrors.value.length === 0));

/** Single source of truth shared by the form controls and the JSON view. */
const schemaDraft = computed<SchemaDraft>(() => ({
  name: name.value,
  label: label.value,
  description: description.value,
  searchability: searchability.value,
  group_label: groupLabel.value,
  group_view_all_label: groupViewAllLabel.value,
  group_view_all_href: groupViewAllHref.value,
  search_group_sort_index: searchSortIndex.value,
  fields: fields.value,
}));

watch(schema, (next) => {
  if (!next) return;
  const sameSchema = hydratedSchemaId.value === next.id;
  if (sameSchema && !draftMatchesSchema(next)) return;
  hydrateSchema(next);
}, { immediate: true });

function hydrateSchema(next: PageSchema) {
  name.value = next.name;
  label.value = next.label;
  description.value = next.description || '';
  searchability.value = next.searchability || 'non-searchable';
  groupLabel.value = next.group_label || '';
  groupViewAllLabel.value = next.group_view_all_label || '';
  groupViewAllHref.value = next.group_view_all_href || '';
  searchSortIndex.value = next.search_group_sort_index ?? null;
  fields.value = (next.fields || []).map((field) => ({ ...field, options: [...(field.options || [])] }));
  selectedIndex.value = 0;
  hydratedSchemaId.value = next.id;
}

function draftMatchesSchema(next: PageSchema) {
  return name.value === next.name
    && label.value === next.label
    && description.value === (next.description || '')
    && searchability.value === (next.searchability || 'non-searchable')
    && groupLabel.value === (next.group_label || '')
    && groupViewAllLabel.value === (next.group_view_all_label || '')
    && groupViewAllHref.value === (next.group_view_all_href || '')
    && searchSortIndex.value === (next.search_group_sort_index ?? null)
    && JSON.stringify(fields.value) === JSON.stringify((next.fields || []).map((field) => ({ ...field, options: [...(field.options || [])] })));
}

function goBack() {
  router.push({ name: 'admin-pages' });
}

function openPageEditor() {
  void router.push({ name: 'admin-page-metafields-editor', params: { schemaId: schemaId.value } });
}

function selectSchema(nextSchema: { id: number }) {
  if (nextSchema.id === schemaId.value) return;
  void router.push({ name: 'admin-page-schema-builder', params: { schemaId: nextSchema.id } });
}

/** Loads a JSON document back into the form controls; saving stays one path. */
function applySchemaJson(draft: SchemaDraft) {
  name.value = draft.name;
  label.value = draft.label;
  description.value = draft.description;
  searchability.value = draft.searchability;
  groupLabel.value = draft.group_label;
  groupViewAllLabel.value = draft.group_view_all_label;
  groupViewAllHref.value = draft.group_view_all_href;
  searchSortIndex.value = draft.search_group_sort_index;
  fields.value = draft.fields;
  selectedIndex.value = Math.max(0, Math.min(selectedIndex.value, draft.fields.length - 1));
  editorView.value = 'form';
  toaster.create({ title: 'JSON applied to the editor', type: 'success' });
}

async function submitCreateSchema() {
  const createdId = await createFlow.submit();
  if (createdId) {
    await router.push({ name: 'admin-page-schema-builder', params: { schemaId: createdId } });
  }
}

function selectField(index: number) {
  selectedIndex.value = index;
}

function addField(type: PageSchemaFieldType = 'text') {
  fields.value = [...fields.value, createDefaultSchemaField(type, fields.value)];
  selectedIndex.value = fields.value.length - 1;
  addFieldDialogOpen.value = false;
}

function duplicateField(index: number) {
  const source = fields.value[index];
  if (!source) return;
  const copy: PageSchemaField = {
    ...source,
    key: `${source.key}_copy`,
    label: `${source.label} copy`,
    options: [...(source.options || [])],
  };
  fields.value = [
    ...fields.value.slice(0, index + 1),
    copy,
    ...fields.value.slice(index + 1),
  ];
  selectedIndex.value = index + 1;
}

function deleteField(index: number) {
  const source = fields.value[index];
  if (!source) return;
  if (!window.confirm(`Delete field "${source.label}"? Existing page values for ${formatMetafieldReference(source.key)} will remain stored but no longer show in this schema editor.`)) return;
  fields.value = fields.value.filter((_, current) => current !== index);
  selectedIndex.value = Math.max(0, Math.min(selectedIndex.value, fields.value.length - 1));
}

function moveField(index: number, direction: -1 | 1) {
  const nextIndex = index + direction;
  if (nextIndex < 0 || nextIndex >= fields.value.length) return;
  const next = [...fields.value];
  const [field] = next.splice(index, 1);
  if (!field) return;
  next.splice(nextIndex, 0, field);
  fields.value = next;
  selectedIndex.value = nextIndex;
}

function updateSelectedField(patch: Partial<PageSchemaField>) {
  if (!selectedField.value) return;
  fields.value = fields.value.map((field, index) => {
    if (index !== selectedIndex.value) return field;
    const next = { ...field, ...patch };
    if (patch.type && patch.type !== field.type) {
      const definition = getPageFieldTypeDefinition(patch.type);
      next.default_value = definition.defaultValue;
      next.options = definition.supportsOptions ? ['Option 1'] : [];
    }
    return next;
  });
}

function updateOptions(value: string) {
  updateSelectedField({
    options: value
      .split('\n')
      .map((option) => option.trim())
      .filter(Boolean),
  });
}

async function saveSchema() {
  if (!schema.value || !canSave.value) return;
  await updateSchema({
    id: schema.value.id,
    data: {
      name: name.value.trim(),
      label: label.value.trim(),
      description: description.value.trim() || null,
      fields: fields.value,
      searchability: searchability.value,
      group_label: groupLabel.value.trim() || null,
      group_view_all_label: groupViewAllLabel.value.trim() || null,
      group_view_all_href: groupViewAllHref.value.trim() || null,
      search_group_sort_index: searchSortIndex.value,
    },
  });
  toaster.create({ title: 'Schema saved', type: 'success' });
}
</script>

<template>
  <main class="schema-builder-root flex min-h-0 flex-1 flex-col overflow-hidden bg-white">
    <AdminEditorTopBar
      :sidebar-open="schemasPanelOpen"
      title="Schema Builder"
      :subtitle="schema?.label || 'Loading schema...'"
      :url-value="''"
      :show-url="false"
      :status-label="validationErrors.length ? `${validationErrors.length} issue${validationErrors.length === 1 ? '' : 's'}` : 'Ready'"
      :can-save="canSave"
      :is-saving="isSaving"
      save-label="Save schema"
      back-label="Back to schemas"
      sidebar-label-open="Close schemas"
      sidebar-label-closed="Open schemas"
      @back="goBack"
      @toggle-sidebar="schemasPanelOpen = !schemasPanelOpen"
      @save="saveSchema"
    >
      <template #status>
        <div class="w-40">
          <UiSegmentedControl
            v-model="editorView"
            :options="[{ label: 'Form', value: 'form' }, { label: 'JSON', value: 'json' }]"
            data-testid="schema-view-toggle"
          />
        </div>
        <UiChip
          :text="validationErrors.length ? `${validationErrors.length} issue${validationErrors.length === 1 ? '' : 's'}` : 'Ready'"
          :variant="validationErrors.length ? 'warn' : 'neutral'"
          size="sm"
        />
        <UiStandardButton label="Edit pages" icon="link-external" variant="outline" size="sm" @click="openPageEditor" />
      </template>
    </AdminEditorTopBar>

    <div v-if="isLoading" class="flex flex-1 items-center justify-center">
      <UiText as="p" variant="p" text="Loading schema..." />
    </div>

    <div v-else-if="error" class="flex flex-1 items-center justify-center p-8">
      <UiInfoBox variant="error">
        <UiText as="p" variant="p-small" :text="`Failed to load schema: ${error.message}`" />
      </UiInfoBox>
    </div>

    <div
      v-else
      class="flex min-h-0 flex-1 overflow-hidden"
    >
      <SchemaNavigatorPanel
        v-if="schemasPanelOpen"
        v-model:search="schemaSearch"
        :schemas="schemas"
        :selected-schema-id="schema?.id"
        :is-loading="isLoadingSchemas"
        @select-schema="selectSchema"
        @create-schema="createFlow.openDialog()"
      />

      <SchemaJsonPanel
        v-if="editorView === 'json'"
        :draft="schemaDraft"
        @apply="applySchemaJson"
      />

      <section v-else class="min-h-0 flex-1 overflow-y-auto border-r border-se-gray-200 bg-white">
        <SchemaSettingsPanel
          v-model:name="name"
          v-model:label="label"
          v-model:description="description"
          v-model:searchability="searchability"
          v-model:group-label="groupLabel"
          v-model:group-view-all-label="groupViewAllLabel"
          v-model:group-view-all-href="groupViewAllHref"
          v-model:search-sort-index="searchSortIndex"
        />

        <div class="px-6 py-5">
          <div class="mb-5 flex items-start justify-between gap-3">
            <div>
              <UiText as="h2" variant="h5" text="Fields" />
              <UiText as="p" variant="p-small" class="mt-1 text-se-gray-700" text="Define the metafields available to pages using this schema." />
            </div>
            <UiStandardButton label="Add field" icon="plus" variant="outline" @click="addFieldDialogOpen = true" />
          </div>

          <UiInfoBox v-if="validationErrors.length" variant="warning" class="mb-4">
            <div class="flex flex-col gap-1">
              <UiText
                v-for="message in validationErrors"
                :key="message"
                as="p"
                variant="p-small"
                :text="message"
              />
            </div>
          </UiInfoBox>

          <SchemaFieldList
            :fields="fields"
            :selected-index="selectedIndex"
            @select-field="selectField"
            @add-field="addFieldDialogOpen = true"
            @duplicate-field="duplicateField"
            @delete-field="deleteField"
            @move-field="moveField"
          />
        </div>
      </section>

      <SchemaFieldDefinitionInspector
        v-if="editorView === 'form'"
        :field="selectedField"
        @update-field="updateSelectedField"
        @update-options="updateOptions"
      />
    </div>

    <AddSchemaFieldDialog
      v-model="addFieldDialogOpen"
      @add-field="addField"
    />

    <CreateSchemaDialog
      v-model="createFlow.open.value"
      :name="createFlow.name.value"
      :label="createFlow.label.value"
      :description="createFlow.description.value"
      :can-create="createFlow.canCreate.value"
      :is-creating="createFlow.isCreating.value"
      @update-name="createFlow.updateName"
      @update-label="createFlow.updateLabel"
      @update-description="createFlow.updateDescription"
      @submit="submitCreateSchema"
    />
  </main>
</template>

<style scoped>
.schema-builder-root {
  height: calc(100dvh - 72px);
}
</style>
